import React from 'react';
let originalTitle = '';
const windowTitles = [
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
];
/**
 * A hook that sets the window title based on the provided title and level.
 * The title is composed of multiple levels, and the final document title is a combination of all active titles joined by ' - '.
 * The title is restored to its previous value when the component using this hook is unmounted.
 *
 * @param {string} title - The title to set for the window.
 * @param {1 | 2 | 3 | 4 | 5} level - The level of the title, where 1 is the top level and 5 is the lowest reserved for edge cases.
 *  - 1 - Reserved for applications only. Usually application name - 'WebEOC'
 *  - 2 - Reserved for applications only. Usually top menu item - 'Workflows'
 *  - 3 - Can be used by applications or libraries. In application, usually tab name - 'Boards'. In library - top menu item - 'Notifications'
 *  - 4 - Can be used by applications or libraries. Usually tab name - 'Sent'
 *  - 5 - Reserved for edge cases. For example for components which are always rendered and never unmounted.
 * @remarks
 * Title levels can be skipped.
 * @example
 * ```typescript
 * useWindowTitle('WebEOC', 1); // Set at entrypoint of the application
 * useWindowTitle('Notifications', 3); // Set by alert-ui library
 * useWindowTitle('Sent', 4); // // Set by alert-ui library
 * // The window title will be 'Sent - Notifications - WebEOC'
 * ```
 */
export function useWindowTitle(title, level) {
    React.useEffect(() => {
        if (!windowTitles.some((title) => !!title)) {
            originalTitle = window.document.title;
        }
        const prevTitle = windowTitles[5 - level];
        windowTitles[5 - level] = title;
        if (windowTitles.some((title) => !!title)) {
            window.document.title = windowTitles.filter((title) => !!title).join(' - ');
        }
        return () => {
            windowTitles[5 - level] = prevTitle;
            if (!windowTitles.some((title) => !!title)) {
                window.document.title = originalTitle;
            }
            else {
                window.document.title = windowTitles.filter((title) => !!title).join(' - ');
            }
        };
    }, [title, level]);
}
